import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import { initPdf, drawHeader, drawFooter, drawPageNumbers, safeFilename, PAGE } from './pdfTheme';
import type { Competition, Category, Discipline } from '../../types/domain';
import type { OverallRanking } from '../../types/results';

interface Args {
  competition: Competition;
  category: Category;
  ranking: OverallRanking;
  disciplines: Discipline[];
}

/**
 * TYP C — Celkové pořadí družstev v kategorii (víceboj). A4 landscape kvůli více sloupcům.
 */
export function generateOverallRanking({ competition, category, ranking, disciplines }: Args): void {
  const doc = initPdf('landscape');
  const dateLoc = `${dayjs(competition.competitionDate).format('D. M. YYYY')}${competition.location ? ', ' + competition.location : ''}`;
  const headerEnd = drawHeader(doc, {
    title: 'Celkové výsledky — družstva',
    competitionName: competition.name,
    competitionDateLocation: dateLoc,
    categoryLabel: category.name,
  });

  const sortedDisc = [...disciplines].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  const head = [[
    'Pořadí',
    'Start. č.',
    'Družstvo',
    ...sortedDisc.map((d) => d.name),
    'Body',
  ]];
  const body = ranking.teams.map((t) => [
    t.finalRank != null ? String(t.finalRank) : '—',
    t.startNumber != null ? String(t.startNumber) : '—',
    t.teamName,
    ...sortedDisc.map((d) => {
      const r = t.perDisciplineRanks?.[d.id];
      return r != null ? String(r) : '—';
    }),
    String(t.totalPoints),
  ]);

  autoTable(doc, {
    startY: headerEnd,
    margin: { left: PAGE.margin, right: PAGE.margin, bottom: 35 },
    head,
    body,
    styles: { font: 'DejaVu', fontSize: 9, cellPadding: 1.5, lineColor: [220, 220, 220] },
    headStyles: { fillColor: [198, 40, 40], textColor: 255, fontStyle: 'bold', halign: 'center', valign: 'middle' },
    alternateRowStyles: { fillColor: [248, 248, 248] },
    columnStyles: {
      0: { halign: 'center', cellWidth: 18, fontStyle: 'bold' },
      1: { halign: 'center', cellWidth: 18 },
      [3 + sortedDisc.length]: { halign: 'center', fontStyle: 'bold', cellWidth: 18 },
    },
    didDrawPage: () =>
      drawFooter(doc, {
        headJudgeName: competition.headJudgeName,
        eventLeaderName: competition.eventLeaderName,
        commissarName: competition.commissarName,
      }),
  });

  drawPageNumbers(doc);

  doc.save(safeFilename(`${competition.shortCode}_${category.name}_celkove.pdf`));
}
